import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faChartColumn, faChartLine, faChartPie, faXmark } from "@fortawesome/free-solid-svg-icons";
import { evaluateCells, displayCell, cellName, colLabel, translateFormula, parseRef } from "./sheetEngine";
import { insertRow, deleteRow, insertCol, deleteCol, toTSV, fromTSV } from "./sheetOps";
import { sortRange, findMatches, replaceAll, removeDuplicates, textToColumns } from "./sheetData";
import { defaultView } from "./sheetModel";
import SheetChart from "./SheetChart";
import SheetRibbon from "./SheetRibbon";

const DEFAULT_COL_W = 92;
const MAX_DIM = 500; // safety cap when paste/insert extend the grid
const EMPTY_LIST = []; // stable empty array (keeps useMemo deps from churning)

/**
 * SheetGrid — controlled spreadsheet grid (workbook + onChange). Excel-like:
 * range selection (drag / shift / Ctrl+A), copy/cut/paste with system-clipboard
 * (TSV) interop, undo/redo, number formats, colors, font size, insert/delete
 * rows & columns, and column resize. Formula evaluation is in sheetEngine.
 */
const norm = (a, b) => ({
  r0: Math.min(a.r, b.r),
  c0: Math.min(a.c, b.c),
  r1: Math.max(a.r, b.r),
  c1: Math.max(a.c, b.c),
});

const SheetGrid = ({ workbook, onChange }) => {
  const active = Math.min(workbook.active || 0, workbook.sheets.length - 1);
  const sheet = workbook.sheets[active];
  const { rows, cols } = workbook;
  const colWidths = workbook.colWidths || {};
  const view = workbook.view || defaultView();
  const freeze = workbook.freeze || { rows: 0, cols: 0 };
  const isProtected = !!workbook.protected;
  const notes = sheet.notes || {};

  const [anchor, setAnchor] = useState({ r: 0, c: 0 });
  const [focus, setFocus] = useState({ r: 0, c: 0 });
  const [editing, setEditing] = useState(null); // { r, c, value } | null
  const [resize, setResize] = useState(null); // { c, w } during a column drag
  const [ribbonTab, setRibbonTab] = useState("Home");
  const [toast, setToast] = useState("");
  const painterArmedRef = useRef(false);
  const toastTimerRef = useRef(null);
  const flash = (msg) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(""), 2600);
  };

  const gridRef = useRef(null);
  const selectingRef = useRef(false);
  const fillingRef = useRef(false);
  const fillSrcRef = useRef(null);
  const fillTargetRef = useRef(null);
  const [fillPreview, setFillPreview] = useState(null);
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const clipRef = useRef(null); // internal rich clipboard

  const sel = norm(anchor, focus);
  const values = useMemo(() => evaluateCells(sheet.cells), [sheet.cells]);

  const rawAt = (r, c) => sheet.cells[cellName(r, c)] || "";
  const fmtAt = (r, c) => sheet.formats[cellName(r, c)] || {};
  const widthOf = (c) => (resize && resize.c === c ? resize.w : colWidths[c] || DEFAULT_COL_W);
  const inSel = (r, c) => r >= sel.r0 && r <= sel.r1 && c >= sel.c0 && c <= sel.c1;
  const fillRect = fillPreview ? norm({ r: sel.r0, c: sel.c0 }, fillPreview) : null;
  const inFill = (r, c) =>
    fillRect && r >= fillRect.r0 && r <= fillRect.r1 && c >= fillRect.c0 && c <= fillRect.c1 && !inSel(r, c);

  // ── undo / redo + emit ──────────────────────────────────────────────────────
  const emit = useCallback(
    (next, record = true) => {
      if (record) {
        undoRef.current.push(workbook);
        if (undoRef.current.length > 200) undoRef.current.shift();
        redoRef.current = [];
      }
      onChange(next);
    },
    [workbook, onChange]
  );
  const undo = () => {
    if (!undoRef.current.length) return;
    redoRef.current.push(workbook);
    onChange(undoRef.current.pop());
    setEditing(null);
  };
  const redo = () => {
    if (!redoRef.current.length) return;
    undoRef.current.push(workbook);
    onChange(redoRef.current.pop());
    setEditing(null);
  };

  const patchSheet = (patch, record = true) => {
    const sheets = workbook.sheets.slice();
    sheets[active] = { ...sheets[active], ...patch };
    emit({ ...workbook, sheets }, record);
  };

  // ── cell + format mutations ─────────────────────────────────────────────────
  const setRaw = (r, c, raw) => {
    if (isProtected) {
      flash("Sheet is protected — unprotect it to edit (Review tab).");
      return;
    }
    const addr = cellName(r, c);
    const cells = { ...sheet.cells };
    if (raw === "" || raw == null) delete cells[addr];
    else cells[addr] = raw;
    patchSheet({ cells });
  };

  const forEachInSel = (fn) => {
    for (let r = sel.r0; r <= sel.r1; r += 1) for (let c = sel.c0; c <= sel.c1; c += 1) fn(r, c);
  };

  const updateSelFormats = (updater) => {
    const formats = { ...sheet.formats };
    forEachInSel((r, c) => {
      const addr = cellName(r, c);
      const next = updater({ ...(formats[addr] || {}) });
      const clean = {};
      Object.keys(next).forEach((k) => {
        const v = next[k];
        if (v !== undefined && v !== null && v !== false && v !== "") clean[k] = v;
      });
      if (Object.keys(clean).length) formats[addr] = clean;
      else delete formats[addr];
    });
    patchSheet({ formats });
    focusGrid();
  };

  const af = fmtAt(anchor.r, anchor.c); // anchor format (drives toolbar toggles)
  const toggleBold = () => updateSelFormats((f) => ({ ...f, b: !af.b }));
  const toggleItalic = () => updateSelFormats((f) => ({ ...f, i: !af.i }));
  const setAlign = (a) => updateSelFormats((f) => ({ ...f, a }));
  const setColor = (key, v) => updateSelFormats((f) => ({ ...f, [key]: v }));
  const setFontSize = (fs) => updateSelFormats((f) => ({ ...f, fs: fs === 13 ? undefined : fs }));
  const changeDecimals = (delta) =>
    updateSelFormats((f) => {
      const nf = f.nf || { t: "num", d: 0 };
      return { ...f, nf: { ...nf, t: nf.t === "gen" || !nf.t ? "num" : nf.t, d: Math.max(0, Math.min(10, (nf.d || 0) + delta)) } };
    });

  // ── extended formatting (Home ribbon) ───────────────────────────────────────
  const toggleUnderline = () => updateSelFormats((f) => ({ ...f, u: !af.u }));
  const setFontFamily = (ff) => updateSelFormats((f) => ({ ...f, ff: ff || undefined }));
  const fontGrow = () => updateSelFormats((f) => ({ ...f, fs: Math.min(72, (f.fs || 13) + 1) }));
  const fontShrink = () => updateSelFormats((f) => ({ ...f, fs: Math.max(8, (f.fs || 13) - 1) }));
  const setVAlign = (va) => updateSelFormats((f) => ({ ...f, va: va === "bottom" ? undefined : va }));
  const changeIndent = (delta) => updateSelFormats((f) => ({ ...f, indent: Math.max(0, Math.min(15, (f.indent || 0) + delta)) || undefined }));
  const toggleWrap = () => updateSelFormats((f) => ({ ...f, wrap: !af.wrap }));

  const applyBorders = (kind) => {
    const formats = { ...sheet.formats };
    forEachInSel((r, c) => {
      const addr = cellName(r, c);
      const cur = { ...(formats[addr] || {}) };
      let bd;
      if (kind === "none") bd = undefined;
      else if (kind === "all") bd = { t: 1, r: 1, b: 1, l: 1 };
      else if (kind === "outer") bd = { t: r === sel.r0 ? 1 : 0, b: r === sel.r1 ? 1 : 0, l: c === sel.c0 ? 1 : 0, r: c === sel.c1 ? 1 : 0 };
      else if (kind === "bottom") bd = { ...(cur.bd || {}), b: 1 };
      else if (kind === "top") bd = { ...(cur.bd || {}), t: 1 };
      if (bd && !(bd.t || bd.r || bd.b || bd.l)) bd = undefined;
      if (bd) cur.bd = bd;
      else delete cur.bd;
      const clean = {};
      Object.keys(cur).forEach((k) => {
        const v = cur[k];
        if (v !== undefined && v !== null && v !== false && v !== "") clean[k] = v;
      });
      if (Object.keys(clean).length) formats[addr] = clean;
      else delete formats[addr];
    });
    patchSheet({ formats });
  };

  // ── merge & center ──────────────────────────────────────────────────────────
  const merges = sheet.merges || EMPTY_LIST;
  const mergeInfo = useMemo(() => {
    const cover = new Set();
    const span = {};
    (sheet.merges || EMPTY_LIST).forEach((m) => {
      span[cellName(m.r0, m.c0)] = { rs: m.r1 - m.r0 + 1, cs: m.c1 - m.c0 + 1 };
      for (let r = m.r0; r <= m.r1; r += 1)
        for (let c = m.c0; c <= m.c1; c += 1) if (!(r === m.r0 && c === m.c0)) cover.add(cellName(r, c));
    });
    return { cover, span };
  }, [sheet.merges]);
  const toggleMerge = () => {
    const idx = merges.findIndex((m) => m.r0 === sel.r0 && m.c0 === sel.c0 && m.r1 === sel.r1 && m.c1 === sel.c1);
    if (idx >= 0) {
      patchSheet({ merges: merges.filter((_, i) => i !== idx) });
      return;
    }
    if (sel.r0 === sel.r1 && sel.c0 === sel.c1) return;
    const kept = merges.filter((m) => !(m.r0 <= sel.r1 && m.r1 >= sel.r0 && m.c0 <= sel.c1 && m.c1 >= sel.c0));
    const formats = { ...sheet.formats };
    const addr = cellName(sel.r0, sel.c0);
    formats[addr] = { ...(formats[addr] || {}), a: "center" };
    patchSheet({ merges: kept.concat({ ...sel }), formats });
  };

  // ── number format dropdown / cell styles ────────────────────────────────────
  const setNumFmtExact = (t) => updateSelFormats((f) => ({ ...f, nf: t === "gen" ? undefined : { t, d: t === "cur" || t === "acc" ? 2 : 0 } }));
  const CELL_STYLES = {
    Normal: {},
    Title: { b: true, fs: 24 },
    "Heading 1": { b: true, fs: 18, fg: "#1f4e79" },
    "Heading 2": { b: true, fs: 15, fg: "#2e74b5" },
    Good: { bg: "#c6efce", fg: "#006100" },
    Bad: { bg: "#ffc7ce", fg: "#9c0006" },
    Neutral: { bg: "#ffeb9c", fg: "#9c6500" },
    Currency: { nf: { t: "cur", d: 2 } },
    Percent: { nf: { t: "pct", d: 0 } },
  };
  const applyCellStyle = (name) => {
    const bundle = CELL_STYLES[name] || {};
    updateSelFormats((f) => (name === "Normal" ? {} : { ...f, ...bundle }));
  };
  const formatAsTable = () => {
    const formats = { ...sheet.formats };
    forEachInSel((r, c) => {
      const addr = cellName(r, c);
      const cur = { ...(formats[addr] || {}) };
      if (r === sel.r0) {
        cur.b = true;
        cur.bg = "#1d6fe0";
        cur.fg = "#ffffff";
      } else {
        if ((r - sel.r0) % 2 === 0) cur.bg = "#eaf1fb";
        else delete cur.bg;
        delete cur.fg;
      }
      formats[addr] = cur;
    });
    patchSheet({ formats });
  };

  // ── conditional formatting ──────────────────────────────────────────────────
  const condRules = sheet.condRules || [];
  const addCondRule = (op) => {
    // eslint-disable-next-line no-alert
    const raw = window.prompt(op === "contains" ? "Highlight cells containing:" : `Highlight cells ${op === "gt" ? "greater than" : op === "lt" ? "less than" : "equal to"}:`);
    if (raw == null) return;
    const palette = { gt: { bg: "#c6efce", fg: "#006100" }, lt: { bg: "#ffc7ce", fg: "#9c0006" }, eq: { bg: "#ffeb9c", fg: "#9c6500" }, contains: { bg: "#bdd7ee", fg: "#1f4e79" } };
    patchSheet({ condRules: condRules.concat({ range: { ...sel }, op, value: raw, ...palette[op] }) });
  };
  const clearCondRules = () => patchSheet({ condRules: [] });
  const condStyleFor = (r, c) => {
    for (let i = condRules.length - 1; i >= 0; i -= 1) {
      const rule = condRules[i];
      const m = rule.range;
      if (r < m.r0 || r > m.r1 || c < m.c0 || c > m.c1) continue;
      const v = values[cellName(r, c)];
      const num = Number(rule.value);
      let hit = false;
      if (rule.op === "gt") hit = typeof v === "number" && v > num;
      else if (rule.op === "lt") hit = typeof v === "number" && v < num;
      else if (rule.op === "eq") hit = String(v) === String(rule.value);
      else if (rule.op === "contains") hit = String(v).toLowerCase().includes(String(rule.value).toLowerCase());
      if (hit) return { bg: rule.bg, fg: rule.fg };
    }
    return null;
  };

  // ── editing tools: AutoSum, Sort, Fill, Clear, Find & Replace ────────────────
  const autoSum = () => {
    if (sel.r1 > sel.r0 && sel.c0 === sel.c1) {
      const tr = Math.min(rows - 1, sel.r1 + 1);
      setRaw(tr, sel.c0, `=SUM(${cellName(sel.r0, sel.c0)}:${cellName(sel.r1, sel.c0)})`);
      select(tr, sel.c0);
    } else if (sel.c1 > sel.c0 && sel.r0 === sel.r1) {
      const tc = Math.min(cols - 1, sel.c1 + 1);
      setRaw(sel.r0, tc, `=SUM(${cellName(sel.r0, sel.c0)}:${cellName(sel.r0, sel.c1)})`);
      select(sel.r0, tc);
    } else {
      let r = anchor.r - 1;
      while (r >= 0 && values[cellName(r, anchor.c)] !== "") r -= 1;
      const top = r + 1;
      startEdit(anchor.r, anchor.c, top < anchor.r ? `=SUM(${cellName(top, anchor.c)}:${cellName(anchor.r - 1, anchor.c)})` : "=SUM()");
    }
  };
  const sortSelection = (asc) => {
    const range = sel.r0 === sel.r1 && sel.c0 === sel.c1 ? { r0: 0, c0: 0, r1: rows - 1, c1: cols - 1 } : sel;
    const next = sortRange(sheet, range, anchor.c, asc);
    patchSheet(next);
  };
  const fillDown = () => doFill({ r0: sel.r0, c0: sel.c0, r1: sel.r0, c1: sel.c1 }, sel.r1, sel.c1);
  const fillRight = () => doFill({ r0: sel.r0, c0: sel.c0, r1: sel.r1, c1: sel.c0 }, sel.r1, sel.c1);
  const clearSel = () => {
    const cells = { ...sheet.cells };
    forEachInSel((r, c) => delete cells[cellName(r, c)]);
    patchSheet({ cells });
  };
  const clearFormats = () => {
    const formats = { ...sheet.formats };
    forEachInSel((r, c) => delete formats[cellName(r, c)]);
    patchSheet({ formats });
  };
  const clearAll = () => {
    const cells = { ...sheet.cells };
    const formats = { ...sheet.formats };
    forEachInSel((r, c) => {
      delete cells[cellName(r, c)];
      delete formats[cellName(r, c)];
    });
    patchSheet({ cells, formats });
  };

  // Find & Replace dialog state
  const [findState, setFindState] = useState(null); // { find, replace, matchCase } | null
  const doReplaceAll = () => {
    if (!findState || !findState.find) return;
    const { cells, count } = replaceAll(sheet, findState.find, findState.replace || "", { matchCase: findState.matchCase });
    if (count) patchSheet({ cells });
    setFindState((s) => ({ ...s, status: `${count} replaced` }));
  };
  const doFindNext = () => {
    if (!findState || !findState.find) return;
    const matches = findMatches(sheet, findState.find, { matchCase: findState.matchCase });
    if (!matches.length) {
      setFindState((s) => ({ ...s, status: "No matches" }));
      return;
    }
    const after = matches.find((a) => {
      const p = parseRef(a);
      return p && (p.row > anchor.r || (p.row === anchor.r && p.col > anchor.c));
    });
    const target = after || matches[0];
    const p = parseRef(target);
    if (p) select(p.row, p.col);
    setFindState((s) => ({ ...s, status: `${matches.length} found` }));
  };

  // row height / column width (Format menu)
  const rowHeights = workbook.rowHeights || {};
  const heightOf = (r) => rowHeights[r] || 24;
  const setRowHeightPrompt = () => {
    // eslint-disable-next-line no-alert
    const v = window.prompt("Row height (px):", String(heightOf(anchor.r)));
    if (v == null) return;
    const h = Math.max(16, Math.min(400, Number(v) || 24));
    emit({ ...workbook, rowHeights: { ...rowHeights, [anchor.r]: h } });
  };
  const setColWidthPrompt = () => {
    // eslint-disable-next-line no-alert
    const v = window.prompt("Column width (px):", String(colWidths[anchor.c] || DEFAULT_COL_W));
    if (v == null) return;
    const w = Math.max(40, Math.min(600, Number(v) || DEFAULT_COL_W));
    emit({ ...workbook, colWidths: { ...colWidths, [anchor.c]: w } });
  };

  // Format Painter — capture the anchor's format, apply to the next selection.
  const [painter, setPainter] = useState(null);
  const startPainter = () => {
    setPainter({ ...af });
    painterArmedRef.current = true; // skip the mouseup of this very click
  };
  const applyPainter = () => {
    if (!painter) return;
    updateSelFormats(() => ({ ...painter }));
    setPainter(null);
  };

  // ── View / Page Layout / Review / Formulas / Data extras ────────────────────
  const setView = (patch) => emit({ ...workbook, view: { ...view, ...patch } });
  const toggleGridlines = () => setView({ gridlines: !view.gridlines });
  const toggleHeadings = () => setView({ headings: !view.headings });
  const toggleFormulaBar = () => setView({ formulaBar: !view.formulaBar });
  const toggleShowFormulas = () => setView({ showFormulas: !view.showFormulas });
  const setZoom = (z) => setView({ zoom: Math.max(50, Math.min(200, z)) });
  const setFreeze = (kind) => {
    const fr =
      kind === "panes" ? { rows: anchor.r, cols: anchor.c }
        : kind === "topRow" ? { rows: 1, cols: 0 }
          : kind === "firstCol" ? { rows: 0, cols: 1 }
            : { rows: 0, cols: 0 };
    emit({ ...workbook, freeze: fr });
  };
  const toggleProtect = () => {
    emit({ ...workbook, protected: !isProtected });
    flash(!isProtected ? "Sheet protected (read-only)" : "Sheet unprotected");
  };
  const addComment = () => {
    const addr = cellName(anchor.r, anchor.c);
    // eslint-disable-next-line no-alert
    const txt = window.prompt("Comment:", notes[addr] || "");
    if (txt == null) return;
    const next = { ...notes };
    if (txt.trim()) next[addr] = txt.trim();
    else delete next[addr];
    patchSheet({ notes: next });
  };
  const insertFunction = (name) => startEdit(anchor.r, anchor.c, `=${name}(`);
  const insertSymbol = (ch) => {
    if (isProtected) return flash("Sheet is protected.");
    return setRaw(anchor.r, anchor.c, (rawAt(anchor.r, anchor.c) || "") + ch);
  };
  const insertLink = () => {
    // eslint-disable-next-line no-alert
    const url = window.prompt("Link URL:", "https://");
    if (url) setRaw(anchor.r, anchor.c, url);
  };
  const usedRange = () => {
    let maxR = 0;
    let maxC = 0;
    Object.keys(sheet.cells).forEach((a) => {
      const p = parseRef(a);
      if (p) {
        maxR = Math.max(maxR, p.row);
        maxC = Math.max(maxC, p.col);
      }
    });
    return { r0: 0, c0: 0, r1: maxR, c1: maxC };
  };
  const doRemoveDuplicates = () => {
    const range = sel.r0 === sel.r1 && sel.c0 === sel.c1 ? usedRange() : sel;
    const { cells, formats, removed } = removeDuplicates(sheet, range);
    patchSheet({ cells, formats });
    flash(`${removed} duplicate row${removed === 1 ? "" : "s"} removed`);
  };
  const doTextToColumns = () => {
    // eslint-disable-next-line no-alert
    const delim = window.prompt("Split text by which character?", ",");
    if (delim == null) return;
    const { cells, cols: newCols } = textToColumns(sheet, sel, delim || ",");
    const sheets = workbook.sheets.slice();
    sheets[active] = { ...sheets[active], cells };
    emit({ ...workbook, sheets, cols: Math.min(MAX_DIM, Math.max(cols, sel.c0 + newCols)) });
  };
  const notAvail = (name) => flash(`“${name}” isn’t available in BluePlanit Sheets yet.`);

  // ── rows / columns ──────────────────────────────────────────────────────────
  const doInsertRow = () => {
    const next = insertRow(sheet, anchor.r);
    const sheets = workbook.sheets.slice();
    sheets[active] = { ...sheets[active], ...next };
    emit({ ...workbook, sheets, rows: Math.min(MAX_DIM, rows + 1) });
  };
  const doDeleteRow = () => {
    if (rows <= 1) return;
    const next = deleteRow(sheet, anchor.r);
    const sheets = workbook.sheets.slice();
    sheets[active] = { ...sheets[active], ...next };
    emit({ ...workbook, sheets, rows: rows - 1 });
  };
  const doInsertCol = () => {
    const next = insertCol(sheet, anchor.c);
    const sheets = workbook.sheets.slice();
    sheets[active] = { ...sheets[active], ...next };
    emit({ ...workbook, sheets, cols: Math.min(MAX_DIM, cols + 1) });
  };
  const doDeleteCol = () => {
    if (cols <= 1) return;
    const next = deleteCol(sheet, anchor.c);
    const sheets = workbook.sheets.slice();
    sheets[active] = { ...sheets[active], ...next };
    emit({ ...workbook, sheets, cols: cols - 1 });
  };
  const addRow = () => emit({ ...workbook, rows: Math.min(MAX_DIM, rows + 1) });
  const addCol = () => emit({ ...workbook, cols: Math.min(MAX_DIM, cols + 1) });

  // ── sheets ──────────────────────────────────────────────────────────────────
  const addSheet = () => {
    const sheets = workbook.sheets.concat({ name: `Sheet${workbook.sheets.length + 1}`, cells: {}, formats: {} });
    emit({ ...workbook, sheets, active: sheets.length - 1 });
    setAnchor({ r: 0, c: 0 });
    setFocus({ r: 0, c: 0 });
  };
  const switchSheet = (i) => {
    setEditing(null);
    emit({ ...workbook, active: i }, false);
    setAnchor({ r: 0, c: 0 });
    setFocus({ r: 0, c: 0 });
  };
  const renameSheet = (i, name) => {
    const sheets = workbook.sheets.slice();
    sheets[i] = { ...sheets[i], name: name || sheets[i].name };
    emit({ ...workbook, sheets });
  };
  const deleteSheet = (i) => {
    if (workbook.sheets.length <= 1) return;
    const sheets = workbook.sheets.filter((_, idx) => idx !== i);
    emit({ ...workbook, sheets, active: Math.max(0, Math.min(active, sheets.length - 1)) });
  };

  // ── clipboard ───────────────────────────────────────────────────────────────
  const copySel = async (cut) => {
    const cellsArr = [];
    const fmtArr = [];
    const tsvRows = [];
    for (let r = sel.r0; r <= sel.r1; r += 1) {
      const cr = [];
      const fr = [];
      const tr = [];
      for (let c = sel.c0; c <= sel.c1; c += 1) {
        cr.push(rawAt(r, c));
        fr.push(fmtAt(r, c));
        tr.push(displayCell(values[cellName(r, c)], fmtAt(r, c).nf));
      }
      cellsArr.push(cr);
      fmtArr.push(fr);
      tsvRows.push(tr);
    }
    const tsv = toTSV(tsvRows);
    clipRef.current = { cells: cellsArr, formats: fmtArr, cut, origin: { r0: sel.r0, c0: sel.c0 }, tsv };
    try {
      await navigator.clipboard.writeText(tsv);
    } catch (e) {
      /* clipboard blocked — internal paste still works */
    }
  };

  const applyPaste = (grid2d, fmt2d, cut, origin) => {
    const h = grid2d.length;
    const w = grid2d[0] ? grid2d[0].length : 0;
    if (!h || !w) return;
    const cells = { ...sheet.cells };
    const formats = { ...sheet.formats };
    // Cut clears the source first (so moving within an overlap still works).
    if (cut && origin) {
      for (let r = 0; r < h; r += 1) for (let c = 0; c < w; c += 1) {
        delete cells[cellName(origin.r0 + r, origin.c0 + c)];
        delete formats[cellName(origin.r0 + r, origin.c0 + c)];
      }
    }
    let needRows = rows;
    let needCols = cols;
    // Relative-reference shift for an internal COPY (Excel adjusts =A1 → =A2 when
    // pasting one row down). A CUT keeps references pointing at the same cells.
    const dRow = !cut && origin ? anchor.r - origin.r0 : 0;
    const dCol = !cut && origin ? anchor.c - origin.c0 : 0;
    for (let r = 0; r < h; r += 1) {
      for (let c = 0; c < w; c += 1) {
        const tr = anchor.r + r;
        const tc = anchor.c + c;
        if (tr >= MAX_DIM || tc >= MAX_DIM) continue;
        const addr = cellName(tr, tc);
        const raw = translateFormula(grid2d[r][c], dRow, dCol);
        if (raw === "" || raw == null) delete cells[addr];
        else cells[addr] = raw;
        if (fmt2d && fmt2d[r] && fmt2d[r][c] && Object.keys(fmt2d[r][c]).length) formats[addr] = { ...fmt2d[r][c] };
        needRows = Math.max(needRows, tr + 1);
        needCols = Math.max(needCols, tc + 1);
      }
    }
    const sheets = workbook.sheets.slice();
    sheets[active] = { ...sheets[active], cells, formats };
    emit({ ...workbook, sheets, rows: Math.min(MAX_DIM, needRows), cols: Math.min(MAX_DIM, needCols) });
    setFocus({ r: Math.min(MAX_DIM - 1, anchor.r + h - 1), c: Math.min(MAX_DIM - 1, anchor.c + w - 1) });
    if (cut) clipRef.current = null;
  };

  const paste = async () => {
    let sys = null;
    try {
      sys = await navigator.clipboard.readText();
    } catch (e) {
      /* blocked */
    }
    const clip = clipRef.current;
    if (clip && (sys == null || sys === clip.tsv)) {
      applyPaste(clip.cells, clip.formats, clip.cut, clip.origin);
    } else if (sys != null && sys !== "") {
      applyPaste(fromTSV(sys), null, false, null);
    }
  };

  // ── fill handle (drag the bottom-right square to fill formulas / series) ─────
  const doFill = (src, tr, tc) => {
    const srcH = src.r1 - src.r0 + 1;
    const srcW = src.c1 - src.c0 + 1;
    const downSpan = tr - src.r1;
    const rightSpan = tc - src.c1;
    if (downSpan <= 0 && rightSpan <= 0) return;
    const vertical = downSpan >= rightSpan;
    const cells = { ...sheet.cells };
    const formats = { ...sheet.formats };
    let needRows = rows;
    let needCols = cols;
    const put = (r, c, sr, sc) => {
      const raw = sheet.cells[cellName(sr, sc)] || "";
      let out = raw;
      if (typeof raw === "string" && raw[0] === "=") out = translateFormula(raw, r - sr, c - sc);
      else if (srcH === 1 && srcW === 1 && raw !== "" && !Number.isNaN(Number(raw))) {
        out = String(Number(raw) + (r - src.r0) + (c - src.c0));
      }
      const addr = cellName(r, c);
      if (out === "" || out == null) delete cells[addr];
      else cells[addr] = out;
      const f = sheet.formats[cellName(sr, sc)];
      if (f && Object.keys(f).length) formats[addr] = { ...f };
      else delete formats[addr];
      needRows = Math.max(needRows, r + 1);
      needCols = Math.max(needCols, c + 1);
    };
    if (vertical) {
      for (let r = src.r1 + 1; r <= tr && r < MAX_DIM; r += 1)
        for (let c = src.c0; c <= src.c1; c += 1) put(r, c, src.r0 + ((r - src.r0) % srcH), c);
    } else {
      for (let c = src.c1 + 1; c <= tc && c < MAX_DIM; c += 1)
        for (let r = src.r0; r <= src.r1; r += 1) put(r, c, r, src.c0 + ((c - src.c0) % srcW));
    }
    const sheets = workbook.sheets.slice();
    sheets[active] = { ...sheets[active], cells, formats };
    emit({ ...workbook, sheets, rows: Math.min(MAX_DIM, needRows), cols: Math.min(MAX_DIM, needCols) });
  };

  // ── charts ──────────────────────────────────────────────────────────────────
  const charts = sheet.charts || [];
  const rangeToChartData = (range) => {
    const { r0, c0, r1, c1 } = range;
    const val = (r, c) => values[cellName(r, c)];
    if (c1 === c0) {
      const labels = [];
      const vals = [];
      for (let r = r0; r <= r1; r += 1) {
        labels.push(String(r - r0 + 1));
        vals.push(Number(val(r, c0)) || 0);
      }
      return { labels, series: [{ name: "Series 1", values: vals }] };
    }
    const headerRow = [];
    for (let c = c0 + 1; c <= c1; c += 1) headerRow.push(val(r0, c));
    const hasHeader = headerRow.some((v) => typeof v === "string" && v !== "");
    const dataR0 = hasHeader ? r0 + 1 : r0;
    const labels = [];
    for (let r = dataR0; r <= r1; r += 1) labels.push(String(val(r, c0) ?? ""));
    const series = [];
    for (let c = c0 + 1; c <= c1; c += 1) {
      const vals = [];
      for (let r = dataR0; r <= r1; r += 1) vals.push(Number(val(r, c)) || 0);
      series.push({ name: hasHeader ? String(val(r0, c) ?? `Series ${c - c0}`) : `Series ${c - c0}`, values: vals });
    }
    return { labels, series };
  };
  const patchCharts = (next) => patchSheet({ charts: next });
  const addChart = () => {
    const id = `ch-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    patchCharts(charts.concat({ id, type: "bar", range: { ...sel }, title: "Chart", x: 60, y: 50 }));
  };
  const setChartType = (id, type) => patchCharts(charts.map((c) => (c.id === id ? { ...c, type } : c)));
  const renameChart = (id, title) => patchCharts(charts.map((c) => (c.id === id ? { ...c, title } : c)));
  const removeChart = (id) => patchCharts(charts.filter((c) => c.id !== id));
  const [chartDrag, setChartDrag] = useState(null); // { id, x, y } during drag
  const startChartDrag = (e, ch) => {
    if (e.target.tagName === "INPUT" || e.target.tagName === "BUTTON") return;
    e.preventDefault();
    const ox = e.clientX - ch.x;
    const oy = e.clientY - ch.y;
    const onMove = (ev) => setChartDrag({ id: ch.id, x: Math.max(0, ev.clientX - ox), y: Math.max(0, ev.clientY - oy) });
    const onUp = (ev) => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      const x = Math.max(0, ev.clientX - ox);
      const y = Math.max(0, ev.clientY - oy);
      setChartDrag(null);
      patchCharts(charts.map((c) => (c.id === ch.id ? { ...c, x, y } : c)));
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // ── live CRM data import (the "better than Excel" bit) ──────────────────────
  const salesData = useSelector((s) => s?.sales?.sales) || [];
  const clientsData = useSelector((s) => s?.clients?.clients) || [];
  const employeesData = useSelector((s) => s?.employee?.AllEmployees) || [];

  const ownerName = (l) => {
    const a = l.assignedTo != null ? l.assignedTo : l.assignTo;
    if (a && typeof a === "object") return [a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || "";
    return a || "";
  };

  const DATASETS = {
    leads: {
      label: "Leads / Pipeline",
      headers: ["Agency", "Stage", "Status", "Owner", "Email", "Phone", "Deal Value"],
      rows: () =>
        salesData.map((l) => [
          l.agencyName || "",
          l.stage || "",
          l.leadStatus || "",
          ownerName(l),
          l.email || l.contactEmail || "",
          l.phone || "",
          l.dealValue != null ? l.dealValue : "",
        ]),
    },
    clients: {
      label: "Clients",
      headers: ["Client", "Email", "Phone", "Status"],
      rows: () => clientsData.map((c) => [c.clientName || c.companyName || "", c.email || "", c.phone || "", c.status || ""]),
    },
    team: {
      label: "Team",
      headers: ["Name", "Email", "Department", "Role"],
      rows: () =>
        employeesData.map((e) => [
          [e.firstname, e.lastname].filter(Boolean).join(" "),
          e.email || "",
          e.department || "",
          e.role || e.accessType || "",
        ]),
    },
  };

  const importData = (key) => {
    const ds = DATASETS[key];
    if (!ds) return;
    const headers = ds.headers;
    const limit = Math.max(0, MAX_DIM - anchor.r - 2);
    const body = ds.rows().slice(0, limit);
    const cells = { ...sheet.cells };
    const formats = { ...sheet.formats };
    let needRows = rows;
    let needCols = cols;
    const put = (r, c, raw, bold) => {
      const addr = cellName(r, c);
      if (raw === "" || raw == null) delete cells[addr];
      else cells[addr] = String(raw);
      if (bold) formats[addr] = { ...(formats[addr] || {}), b: true };
      needRows = Math.max(needRows, r + 1);
      needCols = Math.max(needCols, c + 1);
    };
    headers.forEach((h, ci) => put(anchor.r, anchor.c + ci, h, true));
    body.forEach((row, ri) => row.forEach((v, ci) => put(anchor.r + 1 + ri, anchor.c + ci, v, false)));
    const sheets = workbook.sheets.slice();
    sheets[active] = { ...sheets[active], cells, formats };
    emit({ ...workbook, sheets, rows: Math.min(MAX_DIM, needRows), cols: Math.min(MAX_DIM, needCols) });
  };

  // ── selection + editing ─────────────────────────────────────────────────────
  const focusGrid = () => gridRef.current && gridRef.current.focus();
  const select = (r, c) => {
    setAnchor({ r, c });
    setFocus({ r, c });
  };
  const startEdit = (r, c, initial) => {
    if (isProtected) {
      flash("Sheet is protected — unprotect it to edit (Review tab).");
      return;
    }
    select(r, c);
    setEditing({ r, c, value: initial != null ? initial : rawAt(r, c) });
  };
  const commitEdit = (move) => {
    if (!editing) return;
    const { r, c, value } = editing;
    setEditing(null);
    setRaw(r, c, value);
    if (move === "down") select(Math.min(rows - 1, r + 1), c);
    else if (move === "right") select(r, Math.min(cols - 1, c + 1));
    focusGrid();
  };
  const cancelEdit = () => {
    setEditing(null);
    focusGrid();
  };

  // window mouseup ends a drag-select / column resize / fill / format-paint
  useEffect(() => {
    const up = () => {
      selectingRef.current = false;
      if (fillingRef.current) {
        fillingRef.current = false;
        const t = fillTargetRef.current;
        const src = fillSrcRef.current;
        setFillPreview(null);
        if (t && src) doFill(src, t.r, t.c);
      }
      // Format Painter: apply the captured format to the selection just made
      // (skip the mouseup belonging to the click that armed the painter).
      if (painter) {
        if (painterArmedRef.current) painterArmedRef.current = false;
        else applyPainter();
      }
      setResize((rz) => {
        if (rz) emit({ ...workbook, colWidths: { ...colWidths, [rz.c]: rz.w } });
        return null;
      });
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const moveFocus = (dr, dc, extend) => {
    const nr = Math.max(0, Math.min(rows - 1, focus.r + dr));
    const nc = Math.max(0, Math.min(cols - 1, focus.c + dc));
    setFocus({ r: nr, c: nc });
    if (!extend) setAnchor({ r: nr, c: nc });
  };

  const onGridKeyDown = (e) => {
    if (editing) return;
    const k = e.key;
    const mod = e.ctrlKey || e.metaKey;
    if (mod && (k === "z" || k === "Z")) { e.preventDefault(); undo(); return; }
    if (mod && (k === "y" || k === "Y")) { e.preventDefault(); redo(); return; }
    if (mod && (k === "b" || k === "B")) { e.preventDefault(); toggleBold(); return; }
    if (mod && (k === "i" || k === "I")) { e.preventDefault(); toggleItalic(); return; }
    if (mod && (k === "u" || k === "U")) { e.preventDefault(); toggleUnderline(); return; }
    if (mod && (k === "f" || k === "F")) { e.preventDefault(); setFindState({ find: "", replace: "", matchCase: false }); return; }
    if (mod && (k === "d" || k === "D")) { e.preventDefault(); fillDown(); return; }
    if (mod && (k === "r" || k === "R")) { e.preventDefault(); fillRight(); return; }
    if (mod && (k === "c" || k === "C")) { e.preventDefault(); copySel(false); return; }
    if (mod && (k === "x" || k === "X")) { e.preventDefault(); copySel(true); return; }
    if (mod && (k === "v" || k === "V")) { e.preventDefault(); paste(); return; }
    if (mod && (k === "a" || k === "A")) { e.preventDefault(); setAnchor({ r: 0, c: 0 }); setFocus({ r: rows - 1, c: cols - 1 }); return; }
    if (mod) return; // let Ctrl+S etc. bubble
    if (k === "ArrowUp") { e.preventDefault(); moveFocus(-1, 0, e.shiftKey); }
    else if (k === "ArrowDown") { e.preventDefault(); moveFocus(1, 0, e.shiftKey); }
    else if (k === "ArrowLeft") { e.preventDefault(); moveFocus(0, -1, e.shiftKey); }
    else if (k === "ArrowRight") { e.preventDefault(); moveFocus(0, 1, e.shiftKey); }
    else if (k === "Tab") { e.preventDefault(); select(focus.r, Math.min(cols - 1, focus.c + 1)); }
    else if (k === "Enter") { e.preventDefault(); select(Math.min(rows - 1, focus.r + 1), focus.c); }
    else if (k === "F2") { e.preventDefault(); startEdit(focus.r, focus.c); }
    else if (k === "Delete" || k === "Backspace") { e.preventDefault(); clearSel(); }
    else if (k.length === 1 && !e.altKey) { e.preventDefault(); startEdit(focus.r, focus.c, k); }
  };

  const onEditKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commitEdit("down"); }
    else if (e.key === "Tab") { e.preventDefault(); commitEdit("right"); }
    else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
  };

  // formula bar
  const barValue = editing ? editing.value : rawAt(anchor.r, anchor.c);
  const onBarChange = (e) => setEditing({ r: anchor.r, c: anchor.c, value: e.target.value });
  const onBarKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commitEdit("down"); }
    else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
  };

  // column resize drag
  const startResize = (e, c) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widthOf(c);
    const onMove = (ev) => setResize({ c, w: Math.max(40, startW + (ev.clientX - startX)) });
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // Cumulative offsets for frozen panes (sticky positioning).
  const headH = view.headings ? 24 : 0;
  const rowHeadW = view.headings ? 42 : 0;
  const rowTop = (r) => {
    let t = headH;
    for (let i = 0; i < r; i += 1) t += heightOf(i);
    return t;
  };
  const colLeft = (c) => {
    let l = rowHeadW;
    for (let i = 0; i < c; i += 1) l += widthOf(i);
    return l;
  };

  const ribbonApi = {
    fmt: af,
    painter: !!painter,
    paste,
    copy: () => copySel(false),
    cut: () => copySel(true),
    startPainter,
    fontFamily: setFontFamily,
    fontSize: setFontSize,
    fontGrow,
    fontShrink,
    bold: toggleBold,
    italic: toggleItalic,
    underline: toggleUnderline,
    borders: applyBorders,
    fillColor: (v) => setColor("bg", v),
    fontColor: (v) => setColor("fg", v),
    vAlign: setVAlign,
    hAlign: setAlign,
    indent: changeIndent,
    wrap: toggleWrap,
    merge: toggleMerge,
    numFmt: setNumFmtExact,
    accounting: () => setNumFmtExact("acc"),
    percent: () => setNumFmtExact("pct"),
    comma: () => setNumFmtExact("comma"),
    incDec: () => changeDecimals(1),
    decDec: () => changeDecimals(-1),
    condRule: addCondRule,
    clearCond: clearCondRules,
    formatAsTable,
    cellStyle: applyCellStyle,
    cellStyleNames: Object.keys(CELL_STYLES),
    insertRow: doInsertRow,
    insertCol: doInsertCol,
    deleteRow: doDeleteRow,
    deleteCol: doDeleteCol,
    rowHeight: setRowHeightPrompt,
    colWidth: setColWidthPrompt,
    autoSum,
    sortAsc: () => sortSelection(true),
    sortDesc: () => sortSelection(false),
    find: () => setFindState({ find: "", replace: "", matchCase: false }),
    fillDown,
    fillRight,
    clearAll,
    clearFormats,
    clearContents: clearSel,
    insertChart: addChart,
    importData,
    datasets: DATASETS,
    undo,
    redo,
    // view / page layout
    view,
    freeze,
    protectedSheet: isProtected,
    toggleGridlines,
    toggleHeadings,
    toggleFormulaBar,
    toggleShowFormulas,
    setZoom,
    setFreeze,
    // formulas
    insertFunction,
    // data
    removeDuplicates: doRemoveDuplicates,
    textToColumns: doTextToColumns,
    // review
    toggleProtect,
    addComment,
    // insert
    insertSymbol,
    insertLink,
    // stubs
    notAvail,
  };

  return (
    <div className="webtop-sheet">
      <SheetRibbon api={ribbonApi} tab={ribbonTab} onTab={setRibbonTab} />
      {findState && (
        <div className="webtop-sheet__finddlg">
          <span className="webtop-sheet__findttl">Find &amp; Replace</span>
          <input
            className="webtop-sheet__findinp"
            autoFocus
            placeholder="Find"
            value={findState.find}
            onChange={(e) => setFindState((s) => ({ ...s, find: e.target.value, status: "" }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") doFindNext();
              if (e.key === "Escape") setFindState(null);
            }}
          />
          <input
            className="webtop-sheet__findinp"
            placeholder="Replace with"
            value={findState.replace}
            onChange={(e) => setFindState((s) => ({ ...s, replace: e.target.value }))}
          />
          <label className="webtop-sheet__findcase">
            <input type="checkbox" checked={!!findState.matchCase} onChange={(e) => setFindState((s) => ({ ...s, matchCase: e.target.checked }))} /> Case
          </label>
          <button type="button" onClick={doFindNext}>Find</button>
          <button type="button" onClick={doReplaceAll}>Replace All</button>
          <span className="webtop-sheet__findstatus">{findState.status || ""}</span>
          <button type="button" className="webtop-sheet__findx" onClick={() => setFindState(null)}>✕</button>
        </div>
      )}

      {view.formulaBar && (
        <div className="webtop-sheet__formulabar">
          <span className="webtop-sheet__namebox">{cellName(anchor.r, anchor.c)}</span>
          <span className="webtop-sheet__fx">fx</span>
          <input
            className="webtop-sheet__finput"
            value={barValue}
            onChange={onBarChange}
            onKeyDown={onBarKeyDown}
            placeholder="Enter a value or =formula"
            aria-label="Formula bar"
          />
        </div>
      )}

      <div className="webtop-sheet__gridwrap" ref={gridRef} tabIndex={0} onKeyDown={onGridKeyDown}>
        <table
          className={`webtop-sheet__grid${view.gridlines ? "" : " is-nogridlines"}${view.headings ? "" : " is-noheadings"}`}
          style={{ zoom: view.zoom && view.zoom !== 100 ? view.zoom / 100 : undefined }}
        >
          <thead>
            <tr>
              <th className="webtop-sheet__corner" />
              {Array.from({ length: cols }, (_, c) => (
                <th
                  key={c}
                  className={`webtop-sheet__colhead${c >= sel.c0 && c <= sel.c1 ? " is-sel" : ""}`}
                  style={{ width: widthOf(c), minWidth: widthOf(c), ...(c < freeze.cols ? { position: "sticky", left: colLeft(c), zIndex: 4 } : {}) }}
                >
                  {colLabel(c)}
                  <span className="webtop-sheet__resize" onMouseDown={(e) => startResize(e, c)} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, r) => (
              <tr key={r} style={{ height: heightOf(r) }}>
                <th
                  className={`webtop-sheet__rowhead${r >= sel.r0 && r <= sel.r1 ? " is-sel" : ""}`}
                  style={r < freeze.rows ? { position: "sticky", top: rowTop(r), zIndex: 5 } : undefined}
                >
                  {r + 1}
                </th>
                {Array.from({ length: cols }, (_, c) => {
                  const addr = cellName(r, c);
                  if (mergeInfo.cover.has(addr)) return null; // covered by a merge
                  const spanInfo = mergeInfo.span[addr];
                  const f = fmtAt(r, c);
                  const v = values[addr];
                  const isErr = v != null && typeof v === "object" && v.error;
                  const isNum = typeof v === "number";
                  const isAnchor = anchor.r === r && anchor.c === c;
                  const isEditing = editing && editing.r === r && editing.c === c;
                  const cond = condStyleFor(r, c);
                  const bd = f.bd;
                  const bdColor = "#5b6470";
                  const fzRow = r < freeze.rows;
                  const fzCol = c < freeze.cols;
                  const hasNote = !!notes[addr];
                  const style = {
                    width: widthOf(c),
                    minWidth: widthOf(c),
                    textAlign: f.a || (isNum ? "right" : "left"),
                    verticalAlign: f.va === "top" ? "top" : f.va === "middle" ? "middle" : "bottom",
                    fontWeight: f.b ? 700 : undefined,
                    fontStyle: f.i ? "italic" : undefined,
                    textDecoration: f.u ? "underline" : undefined,
                    fontFamily: f.ff || undefined,
                    color: isErr ? undefined : (cond && cond.fg) || f.fg,
                    background: (cond && cond.bg) || f.bg || (fzRow || fzCol ? "#fff" : undefined),
                    fontSize: f.fs ? `${f.fs}px` : undefined,
                    paddingLeft: f.indent ? `${6 + f.indent * 10}px` : undefined,
                    whiteSpace: f.wrap ? "normal" : undefined,
                    borderTop: bd && bd.t ? `1px solid ${bdColor}` : undefined,
                    borderRight: bd && bd.r ? `1px solid ${bdColor}` : undefined,
                    borderBottom: bd && bd.b ? `1px solid ${bdColor}` : undefined,
                    borderLeft: bd && bd.l ? `1px solid ${bdColor}` : undefined,
                    position: fzRow || fzCol ? "sticky" : undefined,
                    top: fzRow ? rowTop(r) : undefined,
                    left: fzCol ? colLeft(c) : undefined,
                    zIndex: fzRow && fzCol ? 6 : fzRow ? 5 : fzCol ? 4 : undefined,
                  };
                  const cls = [
                    "webtop-sheet__cell",
                    inSel(r, c) ? "is-inrange" : "",
                    isAnchor ? "is-anchor" : "",
                    inFill(r, c) ? "is-fillpreview" : "",
                    isErr ? "is-error" : "",
                    f.wrap ? "is-wrap" : "",
                  ].filter(Boolean).join(" ");
                  return (
                    <td
                      key={c}
                      className={cls}
                      style={style}
                      rowSpan={spanInfo ? spanInfo.rs : undefined}
                      colSpan={spanInfo ? spanInfo.cs : undefined}
                      onMouseDown={(e) => {
                        if (editing) commitEdit();
                        if (e.shiftKey) setFocus({ r, c });
                        else { select(r, c); selectingRef.current = true; }
                      }}
                      onMouseEnter={() => {
                        if (selectingRef.current) setFocus({ r, c });
                        else if (fillingRef.current) {
                          fillTargetRef.current = { r, c };
                          setFillPreview({ r, c });
                        }
                      }}
                      onDoubleClick={() => startEdit(r, c)}
                    >
                      {r === sel.r1 && c === sel.c1 && !isEditing && (
                        <span
                          className="webtop-sheet__fillhandle"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            fillingRef.current = true;
                            fillSrcRef.current = { ...sel };
                            fillTargetRef.current = null;
                          }}
                        />
                      )}
                      {isEditing ? (
                        <input
                          className="webtop-sheet__celledit"
                          autoFocus
                          value={editing.value}
                          onChange={(e) => setEditing({ r, c, value: e.target.value })}
                          onKeyDown={onEditKeyDown}
                          onBlur={() => commitEdit()}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span className="webtop-sheet__celltext">
                          {view.showFormulas ? rawAt(r, c) : displayCell(v, f.nf)}
                        </span>
                      )}
                      {hasNote && <span className="webtop-sheet__note" title={notes[addr]} />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {charts.map((ch) => {
          const pos = chartDrag && chartDrag.id === ch.id ? chartDrag : ch;
          const data = rangeToChartData(ch.range);
          return (
            <div key={ch.id} className="webtop-sheet__chart" style={{ left: pos.x, top: pos.y }}>
              <div className="webtop-sheet__charthead" onMouseDown={(e) => startChartDrag(e, ch)}>
                <input
                  className="webtop-sheet__charttitle"
                  value={ch.title}
                  onChange={(e) => renameChart(ch.id, e.target.value)}
                  aria-label="Chart title"
                />
                <div className="webtop-sheet__chartbtns">
                  <button type="button" className={ch.type === "bar" ? "is-on" : ""} title="Bar" onClick={() => setChartType(ch.id, "bar")}>
                    <FontAwesomeIcon icon={faChartColumn} />
                  </button>
                  <button type="button" className={ch.type === "line" ? "is-on" : ""} title="Line" onClick={() => setChartType(ch.id, "line")}>
                    <FontAwesomeIcon icon={faChartLine} />
                  </button>
                  <button type="button" className={ch.type === "pie" ? "is-on" : ""} title="Pie" onClick={() => setChartType(ch.id, "pie")}>
                    <FontAwesomeIcon icon={faChartPie} />
                  </button>
                  <button type="button" title="Delete chart" onClick={() => removeChart(ch.id)}>
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>
              </div>
              <SheetChart type={ch.type} labels={data.labels} series={data.series} />
            </div>
          );
        })}
      </div>

      <div className="webtop-sheet__tabs">
        {workbook.sheets.map((s, i) => (
          <div
            key={i}
            className={`webtop-sheet__tab${i === active ? " is-active" : ""}`}
            onClick={() => switchSheet(i)}
            onDoubleClick={() => {
              // eslint-disable-next-line no-alert
              const name = window.prompt("Rename sheet", s.name);
              if (name != null) renameSheet(i, name.trim());
            }}
            title="Click to switch, double-click to rename"
          >
            <span>{s.name}</span>
            {workbook.sheets.length > 1 && (
              <button
                type="button"
                className="webtop-sheet__tabx"
                title="Delete sheet"
                onClick={(e) => {
                  e.stopPropagation();
                  // eslint-disable-next-line no-alert
                  if (window.confirm(`Delete sheet "${s.name}"?`)) deleteSheet(i);
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        ))}
        <button type="button" className="webtop-sheet__addsheet" title="Add sheet" onClick={addSheet}>
          <FontAwesomeIcon icon={faPlus} />
        </button>
        <span className="webtop-sheet__tabspacer" />
        <button type="button" className="webtop-sheet__addsheet" title="Add row" onClick={addRow}>+ Row</button>
        <button type="button" className="webtop-sheet__addsheet" title="Add column" onClick={addCol}>+ Col</button>
        <span className="webtop-sheet__tabspacer" />
        {isProtected && <span className="webtop-sheet__lock" title="Sheet is protected (read-only)">🔒 Protected</span>}
        <span className="webtop-sheet__zoomro">{view.zoom || 100}%</span>
      </div>
      {toast && <div className="webtop-sheet__toast">{toast}</div>}
    </div>
  );
};

export default SheetGrid;
